import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { changeBusinessOwnerFormState } from "@/modules/third-party-risk/requests/intake-form-v2";
import { ROUTES } from "@/shared/constants/routes";
import { addNotification } from "@/shared/states/notification";
import { tpraAssessmentsQueryKeys } from "@/shared/constants/QueryKeys";

interface UseRejectFlowParams {
  formId: string;
}

export function useRejectFlow({ formId }: UseRejectFlowParams) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const openRejectModal = useCallback(() => {
    setShowRejectModal(true);
  }, []);

  const closeRejectModal = useCallback(() => {
    setShowRejectModal(false);
    setRejectionReason("");
  }, []);

  const handleReject = useCallback(async () => {
    setIsLoading(true);
    try {
      await changeBusinessOwnerFormState(formId, {
        action: "reject",
        rejection_reason: rejectionReason,
      });

      addNotification({
        type: "success",
        message: "Assessment request rejected successfully",
      });

      await queryClient.invalidateQueries({
        queryKey: tpraAssessmentsQueryKeys.all,
      });

      navigate(`/${ROUTES.THIRD_PARTY_VENDORS_V2}`);
    } catch (err) {
      addNotification({
        type: "error",
        message:
          err instanceof Error
            ? err.message
            : "Failed to reject assessment request",
      });
      setIsLoading(false);
    }
  }, [formId, rejectionReason, navigate, queryClient]);

  return {
    showRejectModal,
    rejectionReason,
    setRejectionReason,
    isLoading,
    openRejectModal,
    closeRejectModal,
    handleReject,
  };
}
